// 인증 전역 상태. 세션(HTTP-only 쿠키) 기반.
//
// 토큰은 JS가 접근할 수 없는 HTTP-only 쿠키에 있으므로 스토어/localStorage에 보관하지
// 않는다. 앱 로드 시 `GET /auth/me`로 세션을 확인해 user/status를 채운다.

import { create } from 'zustand'
import { getMe } from '~/api/auth'
import { registerAuthBridge } from '~/api/client'
import type { UserProfile } from '~/api/types'

/** 세션 확인 진행 상태. unknown=확인 전(초기 깜빡임 방지용). */
export type AuthStatus = 'unknown' | 'authenticated' | 'unauthenticated'

interface AuthState {
    user: UserProfile | null
    status: AuthStatus

    /** 사용자 정보를 갱신한다(로그인/ getMe 등). status=authenticated. */
    setUser(user: UserProfile): void
    /** 인증 상태를 비운다(로그아웃/만료). status=unauthenticated. */
    clear(): void
    /** `/auth/me`로 세션을 확인해 user/status를 동기화한다(앱 로드·콜백 복귀 시). */
    checkSession(): Promise<void>

    /** 인증 여부 헬퍼. */
    isAuthenticated(): boolean
}

export const useAuthStore = create<AuthState>()((set, get) => ({
    user: null,
    status: 'unknown',

    setUser: (user) => set({ user, status: 'authenticated' }),

    clear: () => set({ user: null, status: 'unauthenticated' }),

    checkSession: async () => {
        try {
            const user = await getMe()
            set({ user, status: 'authenticated' })
        } catch {
            set({ user: null, status: 'unauthenticated' })
        }
    },

    isAuthenticated: () => get().status === 'authenticated',
}))

// API 클라이언트가 store에 직접 의존하지 않도록 브리지를 등록한다(순환 의존 방지).
// 세션 만료(refresh 실패) 시 스토어를 비운다.
registerAuthBridge({
    onAuthExpired: () => useAuthStore.getState().clear(),
})
